import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { api } from '../api';
import { useEditor } from '../store';
import { Sheet } from './Sheet';

/**
 * Material aus einem anderen Ordner holen (7.11).
 *
 * Sascha, im Fahrplan: „wenn man etwas aus einem anderen Projekt braucht“ —
 * ein Interview, das schon geschnitten wurde, ein Logo-Bauchbinden-Video, das
 * Musikbett aus der letzten Folge. Geblättert wird wie beim Verbinden eines
 * Ordners; hier stehen zusätzlich die Dateien darin, und angetippt wandern sie
 * in dieses Projekt.
 *
 * Geholt heißt geholt, nicht verlinkt: der Arbeitsdienst legt eine Kopie in
 * den Rohdaten-Ordner dieses Projekts. Ein Verweis auf einen fremden Ordner
 * wäre eine Zeitbombe — wer dort aufräumt, nimmt diesem Schnitt sein Material
 * weg.
 */
export function MedienHolenSheet({
  projectId,
  onClose,
  onFertig,
}: {
  projectId: string;
  onClose: () => void;
  onFertig: () => void;
}): ReactElement {
  const toast = useEditor((s) => s.toast);
  const [stelle, setStelle] = useState<string | undefined>(undefined);
  const [stand, setStand] = useState<Awaited<ReturnType<typeof api.ablageOrdner>> | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Record<string, string>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = useCallback(async (id?: string) => {
    setFehler(null);
    try {
      setStand(await api.ablageOrdner(id));
    } catch (err) {
      setStand(null);
      setFehler(err instanceof Error ? err.message : 'Die Ablage antwortet nicht.');
    }
  }, []);
  useEffect(() => {
    void laden(stelle);
  }, [laden, stelle]);

  const hoeher = stand && stand.id !== stand.wurzel && stand.id.includes('/') ? stand.id.replace(/\/[^/]+$/, '') : null;
  const anzahl = Object.keys(gewaehlt).length;

  const holen = async () => {
    setLaeuft(true);
    try {
      const dateien = Object.entries(gewaehlt).map(([id, name]) => ({ id, name }));
      const { geholt, uebersprungen } = await api.medienHolen(projectId, dateien);
      if (geholt.length === 0) {
        toast('info', uebersprungen[0]?.grund ? `Nichts geholt: ${uebersprungen[0].grund}.` : 'Nichts zu holen.');
      } else {
        toast(
          'success',
          `${geholt.length} Datei${geholt.length === 1 ? '' : 'en'} wird geholt und aufbereitet.` +
            (uebersprungen.length > 0 ? ` ${uebersprungen.length} übersprungen.` : ''),
        );
      }
      onFertig();
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Holen ging nicht.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Sheet title="Aus einem anderen Ordner" onClose={onClose}>
      {fehler ? (
        <p className="hint" data-holen-fehler>
          {fehler} Die Zugangsdaten stehen unter <em>Verwaltung → Ablage</em>.
        </p>
      ) : null}
      {stand ? (
        <>
          <p className="hint" style={{ marginTop: 0 }} data-holen-stelle>
            {stand.anbieter === 'nextcloud' ? 'Nextcloud' : 'Box'} · {stand.id || '/'}
          </p>
          <div className="list">
            {stand.id !== stand.wurzel ? (
              <button className="row" style={{ textAlign: 'left' }} onClick={() => setStelle(hoeher ?? stand.wurzel)}>
                ← Eine Ebene höher
              </button>
            ) : null}
            {stand.ordner.map((o) => (
              <button
                key={o.id}
                className="row"
                data-holen-ordner={o.id}
                style={{ textAlign: 'left' }}
                onClick={() => setStelle(o.id)}
              >
                {o.name}
              </button>
            ))}
            {stand.inhalt.map((d) => {
              const an = d.id in gewaehlt;
              return (
                <button
                  key={d.id}
                  className="row"
                  data-holen-datei={d.id}
                  aria-pressed={an}
                  style={{ textAlign: 'left', borderColor: an ? 'var(--accent)' : 'var(--line)' }}
                  onClick={() =>
                    setGewaehlt((g) => {
                      const neu = { ...g };
                      if (an) delete neu[d.id];
                      else neu[d.id] = d.name;
                      return neu;
                    })
                  }
                >
                  {an ? '✓ ' : ''}
                  {d.name}
                  {d.groesse ? <span className="hint" style={{ marginLeft: 6 }}>{Math.round(d.groesse / 1024 / 1024)} MB</span> : null}
                </button>
              );
            })}
            {stand.ordner.length === 0 && stand.inhalt.length === 0 ? (
              <p className="hint">Hier liegt nichts.</p>
            ) : null}
          </div>
          <button
            className="primary"
            style={{ marginTop: 14 }}
            disabled={laeuft || anzahl === 0}
            onClick={() => void holen()}
            data-holen-los
          >
            {laeuft ? 'Wird geholt…' : anzahl === 0 ? 'Nichts ausgewählt' : `${anzahl} holen`}
          </button>
          <p className="hint">
            Geholt heißt geholt: die Dateien werden in den Ordner dieses Projekts kopiert, nicht nur verlinkt.
          </p>
        </>
      ) : fehler ? null : (
        <p className="hint">Lädt…</p>
      )}
    </Sheet>
  );
}
